import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import logger from 'src/utils/logger';
import { ENDPOINT_PREVIEW_INSTALL_COMMANDS } from '../../preview/endpoints';
import InstallCommandSteps from '../../deploy-agent/InstallCommandSteps';

const cardSx = {
  backgroundColor: '#fff',
  border: '1px solid #E2E8F0',
  borderRadius: '8px',
  boxShadow: 'none',
  boxSizing: 'border-box',
} as const;

type EndpointTechnicalDetailsTabProps = {
  systemWazuhAgentId: string;
};

const EndpointTechnicalDetailsTab: FC<EndpointTechnicalDetailsTabProps> = ({
  systemWazuhAgentId,
}) => {
  const { wait, isPreviewMode } = useEdrDataGate('endpoints');
  const { canWidget } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'endpoint-technical-details',
  );
  const canInstallCommands = canWidget('installCommands');
  const agentId = systemWazuhAgentId.trim();
  const [loading, setLoading] = useState(Boolean(agentId));
  const [error, setError] = useState<string | null>(null);
  const [installCommands, setInstallCommands] = useState<string[]>([]);
  const [copiedMap, setCopiedMap] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!agentId) {
      setInstallCommands([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    if (wait) {
      setLoading(true);
      return undefined;
    }

    if (!canInstallCommands) {
      setInstallCommands([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    if (isPreviewMode) {
      setInstallCommands(ENDPOINT_PREVIEW_INSTALL_COMMANDS);
      setError(null);
      setLoading(false);
      setCopiedMap({});
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setCopiedMap({});

    wazuhAPI
      .getInstallationCommands(agentId)
      .then((data) => {
        if (cancelled) return;
        setInstallCommands(data?.installCommands ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        logger(err, 'error', 'EndpointTechnicalDetailsTab.installCommands');
        setInstallCommands([]);
        setError(err instanceof Error ? err.message : 'Failed to load installation commands');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agentId, wait, isPreviewMode, canInstallCommands]);

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      {canInstallCommands ? (
      <Card sx={cardSx} style={{ padding: 20 }}>
        <Typography
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#1E293B',
            marginBottom: 16,
          }}
        >
          Installation Commands
        </Typography>

        {loading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ paddingTop: 32, paddingBottom: 32, minHeight: 120 }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography style={{ fontSize: 13, color: '#E53E3E' }}>{error}</Typography>
        ) : installCommands.length === 0 ? (
          <Typography style={{ fontSize: 13, color: '#94A3B8' }}>
            No installation commands available for this endpoint.
          </Typography>
        ) : (
          <InstallCommandSteps
            commands={installCommands}
            startStepNumber={1}
            copiedMap={copiedMap}
            onCopy={(groupIndex) =>
              setCopiedMap((prev) => ({ ...prev, [groupIndex]: true }))
            }
          />
        )}
      </Card>
      ) : null}
    </Box>
  );
};

export default EndpointTechnicalDetailsTab;
